import { useState, useEffect, useCallback } from 'react';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Custom hook for managing live quiz sessions
export const useLiveQuizSessions = (autoRefresh = true, refreshInterval = 30000) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }
      
      const data = await response.json();
      setSessions(data.sessions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create session
  const createSession = async (sessionData) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sessionData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create session');
      }
      
      const data = await response.json();
      await fetchSessions(); // Refresh the list
      return data;
    } catch (err) {
      throw err;
    }
  };

  // Update session
  const updateSession = async (sessionId, updates) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update session');
      }
      
      await fetchSessions(); // Refresh the list
    } catch (err) {
      throw err;
    }
  };

  // Delete session
  const deleteSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete session');
      }
      
      await fetchSessions(); // Refresh the list
    } catch (err) {
      throw err;
    }
  };

  // Toggle session status (activate/deactivate)
  const toggleSessionStatus = async (sessionId, currentStatus) => {
    try {
      const action = currentStatus === 'active' ? 'deactivate' : 'activate';
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/${action}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${action} session`);
      }
      
      await fetchSessions(); // Refresh the list
    } catch (err) {
      throw err;
    }
  };

  // Get active sessions (public endpoint)
  const getActiveSessions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/active`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch active sessions');
      }
      
      const data = await response.json();
      return data.sessions || [];
    } catch (err) {
      throw err;
    }
  };

  // Get session participants
  const getSessionParticipants = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/participants`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch participants');
      }
      
      const data = await response.json();
      return data.participants || [];
    } catch (err) {
      throw err;
    }
  };

  // Get session leaderboard
  const getSessionLeaderboard = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/leaderboard`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      
      const data = await response.json();
      return data.leaderboard || [];
    } catch (err) {
      throw err;
    }
  };

  // Join session
  const joinSession = async (sessionId, participantData) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify(participantData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to join session');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  };

  // Leave session
  const leaveSession = async (sessionId, participantData) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify(participantData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to leave session');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  };

  // Complete session (mark as completed to block rejoining)
  const completeSession = async (sessionId, participantData) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify(participantData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to complete session');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  };

  // Update leaderboard entry
  const updateLeaderboardEntry = async (sessionId, participantId, scoreData) => {
    try {
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/participants/${participantId}/leaderboard`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        },
        body: JSON.stringify(scoreData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update leaderboard');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  };

  // Auto-refresh sessions if enabled
  useEffect(() => {
    fetchSessions();
    
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchSessions();
      }, refreshInterval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh, refreshInterval, fetchSessions]);

  return {
    sessions,
    loading,
    error,
    fetchSessions,
    createSession,
    updateSession,
    deleteSession,
    toggleSessionStatus,
    getActiveSessions,
    getSessionParticipants,
    getSessionLeaderboard,
    joinSession,
    leaveSession,
    completeSession,
    updateLeaderboardEntry
  };
};

// Hook for user-side live quiz operations (no authentication required)
export const useLiveQuizUser = (autoRefresh = false, refreshInterval = 10000) => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch active sessions
  const fetchActiveSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/api/live-quiz/active`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch active sessions');
      }
      
      const data = await response.json();
      setActiveSessions(data.sessions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Join session (requires authentication)
  const joinSession = async (sessionId, participantData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authentication token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/api/live-quiz/sessions/${sessionId}/join`, {
        method: 'POST',
        headers,
        body: JSON.stringify(participantData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to join session');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  };

  // Get user's live quiz history
  const getUserHistory = async (userId, deviceId) => {
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      if (deviceId) params.append('deviceId', deviceId);
      
      const response = await fetch(`${API_BASE}/api/live-quiz/history?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user history');
      }
      
      const data = await response.json();
      return data.history || [];
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    fetchActiveSessions();
    
    // Auto-refresh only if enabled
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchActiveSessions();
      }, refreshInterval);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh, refreshInterval, fetchActiveSessions]);

  return {
    activeSessions,
    loading,
    error,
    fetchActiveSessions,
    joinSession,
    getUserHistory
  };
};

